import {
    AccountQuotaConfigRequest,
    AccountQuotaResponse,
    SetAccountQuotaAdditionalRequest
} from '@api/agents/models/curation-agent.api';

/** The backend requires a reset day between 1 and 31; new pools are anchored to the 1st */
export const defaultBillingAnchorDay: number = 1;

/** What a pool is provisioned with when the account first gains the agentic feature - see AMT spec */
export const defaultMonthlyLimit: number = 100;

/**
 * An account's automation agent quota pool, as shown under AI Features in AMT. Only `monthlyLimit`
 * is writable - the rest of the pool is derived by agents-service.
 */
export class AutomationAgentQuotaModel {
    /** Mentions the account's agents may process per period. Editable */
    monthlyLimit: number = defaultMonthlyLimit;
    /** Day of the month the pool resets on. Not surfaced in the UI, but required on save */
    billingAnchorDay: number = defaultBillingAnchorDay;

    /** Top up granted on top of the monthly limit. Editable */
    additionalQuota: number = 0;

    /** Monthly limit plus the top up, as agents-service computes it. Read only */
    totalLimit: number = 0;

    /** Spent so far this period. Read only */
    count: number = 0;
    /** Left this period. Read only, and recomputed locally while the limit is being edited */
    remaining: number = 0;

    /** Epoch ms, UTC midnight of the period start */
    windowStart: number = null;
    /** Epoch ms, UTC midnight of the day the pool resets */
    nextResetDate: number = null;

    /** False until agents-service has provisioned a pool, ie. the GET came back 404 */
    provisioned: boolean = false;

    constructor(apiModel?: AccountQuotaResponse) {
        if (!apiModel) {
            // Nothing provisioned yet, so the read only fields follow the default limit rather than
            // sitting at zero beside it
            this.updateRemaining();

            return;
        }

        this.provisioned = true;
        this.monthlyLimit = apiModel.monthlyLimit;
        this.billingAnchorDay = apiModel.billingAnchorDay ?? defaultBillingAnchorDay;
        this.additionalQuota = apiModel.additionalQuota ?? 0;
        this.totalLimit = apiModel.totalLimit ?? this.monthlyLimit + this.additionalQuota;
        this.count = apiModel.count;
        this.remaining = apiModel.remaining;
        this.windowStart = apiModel.windowStart;
        this.nextResetDate = apiModel.nextResetDate;
    }

    /** Keeps the read only Remaining field honest while the limit or top up is being typed */
    updateRemaining(): void {
        this.totalLimit = (this.monthlyLimit ?? 0) + (this.additionalQuota ?? 0);
        this.remaining = Math.max(0, this.totalLimit - (this.count ?? 0));
    }

    toApiModel(): AccountQuotaConfigRequest {
        return {
            monthlyLimit: this.monthlyLimit,
            billingAnchorDay: this.billingAnchorDay
        };
    }

    /** Body for the separate endpoint the top up is set through */
    toAdditionalQuotaRequest(): SetAccountQuotaAdditionalRequest {
        return { additionalQuota: this.additionalQuota ?? 0 };
    }
}
